/**
 * Image handling utilities for better image loading and error handling
 */

// Base URL للـ API
const API_BASE_URL = "https://omarsanosy211.pythonanywhere.com";

// إنشاء URL كامل للصورة من الـ API response
export const buildImageUrl = (imagePath: string | null): string | null => {
  if (!imagePath) return null;

  // إذا كان URL كامل بالفعل
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // إذا كان relative path
  let cleanPath = imagePath.trim();

  // إزالة الـ slash في البداية إذا موجود لتجنب double slashes
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.substring(1);
  }

  // بناء الـ URL الكامل
  return `${API_BASE_URL}/${cleanPath}`;
};

// إنشاء proxy URL للصور التي لها مشاكل CORS
export const createProxyImageUrl = (originalUrl: string): string => {
  if (!originalUrl || !originalUrl.includes("pythonanywhere.com")) {
    return originalUrl;
  }

  // يمكن استخدام proxy services مثل:
  // return `https://cors-anywhere.herokuapp.com/${originalUrl}`;
  // return `https://api.allorigins.win/raw?url=${encodeURIComponent(originalUrl)}`;

  // في الوقت الحالي، نرجع الـ URL الأصلي
  return originalUrl;
};

// تنظيف وإصلاح URLs
export const sanitizeImageUrl = (url: string | null): string | null => {
  if (!url) return null;

  // إزالة المسافات والأحرف الغير صالحة
  let cleanUrl = url.trim();

  // إصلاح مشاكل الـ double slashes
  cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, "$1");

  // التأكد من أن الـ URL يبدأ بـ protocol صحيح
  if (!cleanUrl.startsWith("http://") && !cleanUrl.startsWith("https://")) {
    // إذا كان يبدأ بـ // فقط
    if (cleanUrl.startsWith("//")) {
      cleanUrl = "https:" + cleanUrl;
    }
    // إذا كان relative URL
    else if (!cleanUrl.startsWith("/")) {
      cleanUrl = "https://" + cleanUrl;
    }
  }

  return cleanUrl;
};

// إنشاء placeholder SVG ديناميكي
export const createPlaceholderSvg = (
  width: number = 64,
  height: number = 64,
  text?: string
): string => {
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" rx="8" fill="#F3F4F6"/>
      <rect x="${width * 0.2}" y="${height * 0.2}" width="${
    width * 0.6
  }" height="${height * 0.4}" rx="4" fill="#E5E7EB"/>
      <circle cx="${width * 0.35}" cy="${height * 0.4}" r="${
    width * 0.05
  }" fill="#D1D5DB"/>
      <path d="M${width * 0.2} ${height * 0.7}L${width * 0.35} ${
    height * 0.55
  }L${width * 0.5} ${height * 0.65}L${width * 0.8} ${height * 0.5}L${
    width * 0.8
  } ${height * 0.7}Z" fill="#D1D5DB"/>
      ${
        text
          ? `<text x="${width / 2}" y="${
              height * 0.85
            }" text-anchor="middle" font-size="${
              width * 0.08
            }" fill="#9CA3AF">${text}</text>`
          : ""
      }
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// فحص نوع الصورة من URL
export const getImageTypeFromUrl = (url: string): string | null => {
  if (!url) return null;

  const extension = url.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    default:
      return null;
  }
};

// تحديد مقاس الصورة
export const getImageDimensions = (
  url: string
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};

// تشخيص مشاكل الصور الشائعة
export const diagnoseImageError = (url: string): string[] => {
  const issues: string[] = [];

  if (!url) {
    issues.push("URL الصورة فارغ");
    return issues;
  }

  // فحص pythonanywhere specific issues
  if (url.includes("pythonanywhere.com")) {
    issues.push("مشكلة محتملة في خادم PythonAnywhere");
    issues.push("قد تحتاج إعدادات CORS إضافية");
    issues.push("جرب الوصول للرابط مباشرة في المتصفح");
  }

  // فحص HTTPS/HTTP mixing
  if (window.location.protocol === "https:" && url.startsWith("http:")) {
    issues.push("Mixed content: HTTPS page loading HTTP image");
  }

  // فحص URL formatting
  if (url.includes("//") && !url.startsWith("http")) {
    issues.push("URL غير مُنسق بشكل صحيح");
  }

  return issues;
};

// إحصائيات تحميل الصور
export class ImageLoadingStats {
  private static instance: ImageLoadingStats;
  private stats = {
    totalAttempts: 0,
    successfulLoads: 0,
    failedLoads: 0,
    pythonanywhereFails: 0,
    corsErrors: 0,
  };

  static getInstance(): ImageLoadingStats {
    if (!ImageLoadingStats.instance) {
      ImageLoadingStats.instance = new ImageLoadingStats();
    }
    return ImageLoadingStats.instance;
  }

  recordAttempt() {
    this.stats.totalAttempts++;
  }

  recordSuccess() {
    this.stats.successfulLoads++;
  }

  recordFailure(url: string) {
    this.stats.failedLoads++;

    if (url.includes("pythonanywhere.com")) {
      this.stats.pythonanywhereFails++;
    }

    if (url.includes("CORS")) {
      this.stats.corsErrors++;
    }
  }

  getStats() {
    return {
      ...this.stats,
      successRate:
        this.stats.totalAttempts > 0
          ? (
              (this.stats.successfulLoads / this.stats.totalAttempts) *
              100
            ).toFixed(1) + "%"
          : "0%",
    };
  }
}
